var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FaXTwitter } from 'react-icons/fa6';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { brandEndpointIds } from 'root/src/shared/brand/brand.constants';
import brandSettingsSelector from 'root/src/client/logic/form/selectors/brandSettingsSelector';
import { useAppDispatch, useAppSelector } from 'root/src/client/logic/helpers/useStore';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { useSettingsDialogClose } from './SettingsDialogRow';
const URL_REGEX = /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,11}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)$/;
const HANDLE_REGEX = /^[A-Za-z0-9_]{1,15}$/;
// Backend stores a twitter.com link; the UI only takes the handle.
const extractHandle = (value) => (value !== null && value !== void 0 ? value : '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^(twitter|x)\.com\//, '')
    .replace(/\/$/, '')
    .trim();
const publisherSchema = z.object({
    brandName: z.string().min(1, 'Publisher name is required'),
    brandWebsite: z.string().min(1, 'Website is required').regex(URL_REGEX, 'Enter a valid URL, for example https://example.com'),
    twitterHandle: z.string().optional().refine(value => !value || HANDLE_REGEX.test(value), 'Use up to 15 letters, numbers or underscore'),
});
export const PublisherSettingsSection = () => {
    var _a, _b, _c, _d, _e;
    const dispatch = useAppDispatch();
    const closeDialog = useSettingsDialogClose();
    const current = useAppSelector(brandSettingsSelector);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(publisherSchema),
        defaultValues: { brandName: '', brandWebsite: '', twitterHandle: '' },
    });
    // Re-seed when the stored brand settings load/change.
    const currentHandle = extractHandle(current === null || current === void 0 ? void 0 : current.brandTwitter);
    const currentKey = `${(_a = current === null || current === void 0 ? void 0 : current.brandName) !== null && _a !== void 0 ? _a : ''}|${(_b = current === null || current === void 0 ? void 0 : current.brandWebsite) !== null && _b !== void 0 ? _b : ''}|${currentHandle}`;
    useEffect(() => {
        var _a, _b;
        reset({
            brandName: (_a = current === null || current === void 0 ? void 0 : current.brandName) !== null && _a !== void 0 ? _a : '',
            brandWebsite: (_b = current === null || current === void 0 ? void 0 : current.brandWebsite) !== null && _b !== void 0 ? _b : '',
            twitterHandle: currentHandle,
        });
    }, [currentKey]);
    const onSubmit = handleSubmit((values) => __awaiter(void 0, void 0, void 0, function* () {
        var _f;
        setFailed(false);
        const handle = (_f = values.twitterHandle) === null || _f === void 0 ? void 0 : _f.trim();
        const payload = {
            brandName: values.brandName,
            brandWebsite: values.brandWebsite,
            brandTwitter: handle ? `twitter.com/${handle}` : '',
        };
        const toastId = toast.loading('Saving publisher settings...');
        try {
            const result = yield dispatch(apiRequest(brandEndpointIds.SET_BRAND_SETTINGS, payload));
            if (result === null || result === void 0 ? void 0 : result.error) {
                throw result.error;
            }
            setSaved(true);
            closeDialog();
            // Fire the confirmation just after the dialog closes (0.15s close animation) so the toast
            // lives on the page instead of being torn down together with the closing dialog layer.
            setTimeout(() => toast.success('Publisher settings saved', { id: toastId }), 200);
        }
        catch (e) {
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
            toast.error('Could not save publisher settings', { id: toastId, description: 'Please try again.' });
        }
    }), () => {
        toast.error('Check the form', { description: 'Fix the highlighted fields.' });
    });
    return (<div>
			<h3 className="mb-1 text-sm font-semibold">Publisher Settings</h3>
			<p className="mb-4 text-xs text-muted-foreground">Your public brand profile shown across DareDrop.</p>
			<form id="publisher-settings-form" onSubmit={onSubmit} className="space-y-3">
				<Field label="Publisher name" htmlFor="brand-name" error={(_c = errors.brandName) === null || _c === void 0 ? void 0 : _c.message}>
					<Input id="brand-name" error={!!errors.brandName} {...register('brandName')}/>
				</Field>
				<Field label="Website" htmlFor="brand-website" error={(_d = errors.brandWebsite) === null || _d === void 0 ? void 0 : _d.message}>
					<Input id="brand-website" placeholder="https://example.com" error={!!errors.brandWebsite} {...register('brandWebsite')}/>
				</Field>
				<Field label="Twitter / X" htmlFor="brand-twitter" error={(_e = errors.twitterHandle) === null || _e === void 0 ? void 0 : _e.message}>
					<div className={cn('flex h-9 items-center gap-1.5 rounded-lg border border-solid bg-white/[0.03] px-1.5 transition-colors focus-within:border-primary/30', errors.twitterHandle ? 'border-destructive/60' : 'border-white/[0.06]')}>
						<span className="flex shrink-0 select-none items-center gap-1 rounded-md bg-white/[0.06] px-2 py-1 text-xs font-medium text-muted-foreground">
							<FaXTwitter className="h-3 w-3"/>
							x.com/
						</span>
						<input id="brand-twitter" placeholder="username" className="h-full w-full bg-transparent pr-1.5 text-sm outline-none placeholder:text-muted-foreground/30" {...register('twitterHandle')}/>
					</div>
				</Field>
			</form>
			<div className="mt-5 flex justify-end">
				<Button type="submit" form="publisher-settings-form" size="sm" variant="brand" loading={isSubmitting} success={saved} error={failed} className="h-9 min-w-[110px] rounded-lg text-xs">
					Save
				</Button>
			</div>
		</div>);
};
